import React, { useContext, useEffect, useState } from 'react'
import { Flex, Select, Input } from "antd";
import { InputNumber } from "antd";
import { useViewerStore } from "../../../../store/store";
import { useCurrentUser } from "../../../../contexts/UserContext";
import { FaRegCheckCircle } from "react-icons/fa";
import cloneDeep from "lodash/cloneDeep";
import { BuildingDesignData } from '../../../../@types/types';

interface AdditionalRoofAttributesProps {
    selectedBuilding: BuildingDesignData,
    setSelectedBuilding: React.Dispatch<React.SetStateAction<BuildingDesignData | undefined>>;
    showInactive: any,
    readOnly: any,
}

const { Option } = Select;
const AdditionalRoofAttributes: React.FC<AdditionalRoofAttributesProps> = ({
    selectedBuilding,
    setSelectedBuilding,
    showInactive,
    readOnly,
}) => {

    const { sceneViewer, masterDataList } = useViewerStore();
    const { user } = useCurrentUser();
    const [localBuildingUpdated, setLocalBuildingUpdated] = useState<BuildingDesignData>();
    const YesOrNoOptions = [{ value: 1, label: "Yes" }, { value: 2, label: "No" }];
    const getValue = (list: any, label: string) => list.find((item: any) => item.label === label)?.value;
    const getLabel = (list: any, value: number) => list.find((item: any) => item.value === value)?.label;

    useEffect(() => {
        if (!selectedBuilding) return;
        let hasChanges = false;
        const newBuildData = cloneDeep(selectedBuilding);
        // Converts the saved labels to the corresponding value in the master data
        newBuildData.Roofs.forEach(roof => {
            if (roof.Attachment_Manufacturer) {
                const newValue = getValue(masterDataList?.AttachmentManufacturer, roof.Attachment_Manufacturer);
                if (newValue && newValue !== roof.Attachment_Manufacturer) {
                    roof.Attachment_Manufacturer = newValue;
                    hasChanges = true;
                }
            }
            if (roof.Rail_Manufacturer) {
                const newValue = getValue(masterDataList?.RailManufacturer, roof.Rail_Manufacturer);
                if (newValue && newValue !== roof.Rail_Manufacturer) {
                    roof.Rail_Manufacturer = newValue;
                    hasChanges = true;
                }
            }
        });
        if (hasChanges) setLocalBuildingUpdated(newBuildData);
    }, [selectedBuilding, masterDataList]);

    const handleChange = (field: string, value: any, index: number) => {
        useViewerStore.setState({ isRoofChange: true });
        const updatedRoofs = [...selectedBuilding.Roofs];
        updatedRoofs[index] = {
            ...updatedRoofs[index],
            [field]: value,
            ...(field === "Rail_Manufacturer" && { Rail_Type: "" }),
            ...(field === "Attachment_Manufacturer" && { Attachment_Type: "" }),
        };
        setLocalBuildingUpdated((prev) => {
            if (!prev) return prev; // or handle initializing default object here
            const updated = {
                ...prev,
                Roofs: [...updatedRoofs],
            };
            return updated;
        });
        // Replace values from masterdata with labels
        if (field === "Rail_Manufacturer"){
            updatedRoofs[index].Rail_Manufacturer = getLabel(masterDataList?.RailManufacturer, value);
        } else if (field === "Attachment_Manufacturer"){
            updatedRoofs[index].Attachment_Manufacturer = getLabel(masterDataList?.AttachmentManufacturer, value);
        }
        setSelectedBuilding((prev) => {
            if (!prev) return prev; // or return undefined
            return {
                ...prev,
                Roofs: [...updatedRoofs],
            };
        });
    };

    const highlightRoof = (roofName: string, value: boolean) => {
        if (roofName.includes('Roof ')) {
            const roofId = Number(roofName.split(' ')[1]);
            sceneViewer.highlightRoof(roofId, value);
        }
    }
    return (
        <>
            <span className="tab__column tab__column--title">Additional Roof Information</span>
            {selectedBuilding && user && selectedBuilding.Roofs.map((item, index) => (
                <div key={item.Name}
                    onMouseEnter={() => highlightRoof(item.Name, true)}
                    onMouseLeave={() => highlightRoof(item.Name, false)}>
                    <>
                        {(showInactive || item.Number_of_Modules > 0) && (
                            <div className="tab__column obst-edit-mask" key={index}>
                                <span className="tab__column--subtitle">
                                    <Flex align="center">
                                        {item.Name}
                                        {item.Number_of_Modules > 0 && (
                                            <FaRegCheckCircle color="green" style={{ marginLeft: 10 }} />
                                        )}
                                    </Flex>
                                </span>

                                <Flex align="center" justify="space-between" className="small_input_flex">
                                    <div className="input_label">
                                        <span className="tab__input--label small_input_label">Existing Solar Panels:</span>
                                    </div>
                                    <Select
                                        className="small-input-feild"
                                        value={item.Existing_Solar_Panels || ""}
                                        onChange={(value) => handleChange("Existing_Solar_Panels", value, index)}
                                        disabled={readOnly} >
                                        {YesOrNoOptions.map((option) => (
                                            <Option key={option.value} value={option.label}>{option.label}</Option>
                                        ))}
                                    </Select>
                                </Flex>

                                <Flex align="center" justify="space-between" className="small_input_flex" gap={5}>
                                    <div className="input_label">
                                        <span className="tab__input--label small_input_label">Existing Solar Area:</span>
                                    </div>
                                    <Input
                                        className="small-input-feild"
                                        value={item.Existing_Solar_Panels === 'No' ? '0' : item.Existing_Solar_Area ?? ''}
                                        onChange={(e) => handleChange("Existing_Solar_Area", e.target.value, index)}
                                        disabled={readOnly || !item.Existing_Solar_Panels || item.Existing_Solar_Panels === 'No'}
                                        style={{ width: "42%" }} />
                                    <div>
                                        <span className="tab__input--label small_input_label">Sq Ft</span>
                                    </div>
                                </Flex>

                                <Flex align="center" justify="space-between" className="small_input_flex">
                                    <div className="input_label">
                                        <span className="tab__input--label small_input_label">PV Module Type:</span>
                                    </div>
                                    <Select
                                        className="small-input-feild"
                                        value={item.PV_Module_Type ?? 0}
                                        onChange={(value) => handleChange("PV_Module_Type", value, index)}
                                        disabled={readOnly}>
                                        {masterDataList?.PVModuleType?.map((option) => (
                                            <Option key={option.value} value={option.label}>{option.label}</Option>
                                        ))}
                                    </Select>
                                </Flex>

                                <Flex align="center" justify="space-between" className="small_input_flex">
                                    <div className="input_label">
                                        <span className="tab__input--label small_input_label">Rail Manufacturer:</span>
                                    </div>
                                    <Select
                                        className="small-input-feild"
                                        value={item.Rail_Manufacturer}
                                        onChange={(value) => handleChange("Rail_Manufacturer", value, index)}
                                        disabled={readOnly}>
                                        {masterDataList?.RailManufacturer?.map((option) => (
                                            <Option key={option.value} value={option.value}>{option.label}</Option>
                                        ))}
                                    </Select>
                                </Flex>

                                <Flex align="center" justify="space-between" className="small_input_flex">
                                    <div className="input_label">
                                        <span className="tab__input--label small_input_label">Rail Type:</span>
                                    </div>
                                    <Select className="small-input-feild"
                                        value={item.Rail_Type || ""}
                                        onChange={(value) => handleChange("Rail_Type", value, index)}
                                        disabled={readOnly}>
                                        {/* Rail Type dropdown options are dependant to the value of selected Rail Manufacturer */}
                                        {(masterDataList?.RailType || [])
                                            .filter(type => type.value === parseInt(localBuildingUpdated?.Roofs[index]?.Rail_Manufacturer || ''))
                                            .map((option, index) => (
                                                <Option key={`${option.value}-${index}`} value={option.label}>
                                                    {option.label}
                                                </Option>
                                            ))
                                        }
                                    </Select>
                                </Flex>

                                <Flex align="center" justify="space-between" className="small_input_flex">
                                    <div className="input_label">
                                        <span className="tab__input--label small_input_label">Roof Covering:</span>
                                    </div>
                                    <Select
                                        className="small-input-feild"
                                        value={item.Roof_Covering || ""}
                                        onChange={(value) => handleChange("Roof_Covering", value, index)}
                                        disabled={readOnly}>
                                        {masterDataList?.RoofCovering?.map((option) => (
                                            <Option key={option.value} value={option.label}>{option.label}</Option>
                                        ))}
                                    </Select>
                                </Flex>

                                <Flex align="center" justify="space-between" className="small_input_flex">
                                    <div className="input_label">
                                        <span className="tab__input--label small_input_label">Attachment Manufacturer:</span>
                                    </div>
                                    <Select
                                        className="small-input-feild"
                                        value={item.Attachment_Manufacturer}
                                        onChange={(value) => handleChange("Attachment_Manufacturer", value, index)}
                                        disabled={readOnly}>
                                        {masterDataList?.AttachmentManufacturer?.map((option) => (
                                            <Option key={option.value} value={option.value}>{option.label}</Option>
                                        ))}
                                    </Select>
                                </Flex>

                                <Flex align="center" justify="space-between" className="small_input_flex">
                                    <div className="input_label">
                                        <span className="tab__input--label small_input_label">Attachment Type:</span>
                                    </div>
                                    <Select
                                        className="small-input-feild"
                                        value={item.Attachment_Type}
                                        onChange={(value) => handleChange("Attachment_Type", value, index)}
                                        disabled={readOnly}>
                                        {/* Attachment Type dropdown options are dependant to the value of selected Attachment Manufacturer */}
                                        {(masterDataList?.AttachmentType ?? [])
                                            .filter(type => type.value === parseInt(localBuildingUpdated?.Roofs[index]?.Attachment_Manufacturer || ''))
                                            .map((option, index) => (
                                                <Option key={`${option.value}-${index}`} value={option.label} >
                                                    {option.label}
                                                </Option>
                                            ))}
                                    </Select>
                                </Flex>

                                <Flex align="center" justify="space-between" className="small_input_flex">
                                    <div className="input_label">
                                        <span className="tab__input--label small_input_label">
                                            Number of Stories:
                                        </span>
                                    </div>
                                    <InputNumber 
                                        precision={0}
                                        className="small-input-feild"
                                        value={item.Number_Of_Stories || ""}
                                        onChange={(value) => handleChange("Number_Of_Stories", value, index)} />
                                </Flex>

                                <Flex align="center" justify="space-between" className="small_input_flex">
                                    <div className="input_label">
                                        <span className="tab__input--label small_input_label">Existing Damage:</span>
                                    </div>
                                    <Select
                                        className="small-input-feild"
                                        value={item.Existing_Damage || ""}
                                        onChange={(value) => handleChange("Existing_Damage", value, index)}
                                        disabled={readOnly}>
                                        {YesOrNoOptions.map((option) => (
                                            <Option key={option.value} value={option.label}>{option.label}</Option>
                                        ))}
                                    </Select>
                                </Flex>

                                <Flex align="center" justify="space-between" className="small_input_flex">
                                    <div className="input_label">
                                        <span className="tab__input--label small_input_label">Truss Type:</span>
                                    </div>
                                    <Select
                                        className="small-input-feild"
                                        value={item.Truss_Type || ""}
                                        onChange={(value) => handleChange("Truss_Type", value, index)}
                                        disabled={readOnly || (item.Structure_Type_Id !== 2 && item.Structure_Type_Id !== 4)}>
                                        {masterDataList?.TrussType?.map((option) => (
                                            <Option key={option.value} value={option.label}>{(item.Structure_Type_Id === 2 || item.Structure_Type_Id === 4) ? option.label : ''}</Option>
                                        ))}
                                    </Select>
                                </Flex>
                            </div>)}
                    </>
                </div>
            ))}
        </>
    );
};

export default AdditionalRoofAttributes;
